import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';

/**
 * The owner's portfolio list — split out of what used to be the OwnerHome
 * dashboard when that page narrowed to disbursements and documents only
 * (see OwnerHome.tsx). Moved here rather than deleted: it is still the only
 * way to reach a single property's detail page
 * (`/portal/owner/properties/:id`), and removing it would have made that
 * route unreachable from the UI.
 *
 * Same unfiltered-query reasoning as the rest of this portal:
 * `properties_owner_select` matches an ownership period covering today, so
 * ending an ownership removes a property from this page the next day with
 * no `ownerId` filter to get wrong.
 */
interface PropertyRow {
  id: string;
  name: string;
  propertyType: string;
  address: { line1: string; city: string; state: string; postalCode: string };
  unitCount: number;
}

interface OwnershipRow {
  id: string;
  propertyId: string;
  basisPoints: number;
  /** Already formatted by the API as e.g. "33.34". Displayed, never parsed. */
  percent: string;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
}

export function OwnerPropertyList() {
  const navigate = useNavigate();

  const {
    data: properties,
    isLoading,
    error,
  } = useList<PropertyRow>({
    resource: 'properties',
    pagination: { mode: 'off' },
  });

  const { data: ownerships } = useList<OwnershipRow>({
    resource: 'ownerships',
    pagination: { mode: 'off' },
  });

  const shareFor = (propertyId: string) =>
    ownerships?.data.find((row) => row.propertyId === propertyId && row.isCurrent)?.percent ?? null;

  const columns: Column<PropertyRow>[] = [
    {
      key: 'name',
      header: 'Property',
      render: (row) => (
        <>
          <strong>{row.name}</strong>
          <div className="muted">
            {row.address.line1}, {row.address.city} {row.address.postalCode}
          </div>
        </>
      ),
    },
    { key: 'type', header: 'Type', render: (row) => humanizeEnum(row.propertyType) },
    { key: 'units', header: 'Units', numeric: true, render: (row) => row.unitCount },
    {
      key: 'share',
      header: 'Your share',
      numeric: true,
      render: (row) => {
        const percent = shareFor(row.id);
        return percent === null ? '—' : `${percent}%`;
      },
    },
  ];

  return (
    <section>
      <h1>Your properties</h1>
      <p className="muted">
        Properties you currently hold. If something is missing, notify the property management
        office.
      </p>

      <Bento>
        <BentoCell span="full">
          <DataTable
            columns={columns}
            rows={properties?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={isLoading}
            error={error}
            onRowClick={(row) => void navigate(`/portal/owner/properties/${row.id}`)}
            emptyMessage="No properties are currently held under this account."
          />
        </BentoCell>
      </Bento>
    </section>
  );
}
